import { Scene, MeshBuilder, Vector3, Color4, Color3, LinesMesh } from '@babylonjs/core';
import { MeshAssembly } from '../../MeshAssembly';
import MeshFactory from '../MeshFactory';
import { LineConfiguration } from '../../configuration/interfaces/LineConfiguration';
import { TransformPosition } from '../../../interfaces';

export default class LineBuilder {
	constructor(private meshFactory: MeshFactory, private scene: Scene) {}

	/**
	 * Create line.
	 */
	public createLine(
		points: Array<TransformPosition>,
		color: { r?: number; g?: number; b?: number } | Array<{ r?: number; g?: number; b?: number }>,
		configuration: LineConfiguration = new Object() as LineConfiguration,
		id?: string
	): MeshAssembly {
		const assembly = this.meshFactory.createEmpty(id);
		const vectors =
			points.length > 0 ? points.map((p) => new Vector3(p.x, p.y, p.z)) : [new Vector3(0, 0, 0)];
		const colors = this.getColors(color, vectors.length);
		const { isDashed = false, dashSize = 5, gapSize = 3, dashNb = 50 } = configuration;

		let line: LinesMesh;
		if (!isDashed) {
			line = MeshBuilder.CreateLines(
				'Line',
				{ points: vectors, updatable: true, colors },
				this.scene
			);
		} else {
			line = MeshBuilder.CreateDashedLines(
				'Line',
				{ points: vectors, updatable: true, dashSize, gapSize, dashNb },
				this.scene
			);
			if (colors.length > 0) {
				line.color = new Color3(colors[0].r, colors[0].g, colors[0].b);
			}
		}
		line.isVisible = false;
		line.isPickable = false;
		line.alwaysSelectAsActiveMesh = true;
		line.parent = assembly.transformNode;
		line.layerMask = 1;
		return assembly;
	}

	/**
	 * Modify Line.
	 */
	public modifyLine(
		assembly: MeshAssembly,
		points: Array<TransformPosition>,
		color: { r?: number; g?: number; b?: number } | Array<{ r?: number; g?: number; b?: number }>,
		isDashed?: boolean
	): void {
		const line = assembly.transformNode.getChildren()[0] as LinesMesh;
		const vectors = points.map((p) => new Vector3(p.x, p.y, p.z));
		if (!isDashed) {
			const length = vectors ? vectors.length : line.getTotalVertices();
			const colors: Color4[] | undefined = color ? this.getColors(color, length) : undefined;
			MeshBuilder.CreateLines('Lines', { points: vectors, colors, instance: line });
		} else {
			MeshBuilder.CreateDashedLines('DashedLines', { points: vectors, instance: line });
			if (color) {
				const c = Array.isArray(color) ? color[0] : color;
				line.color = new Color3(c.r, c.g, c.b);
			}
		}
	}

	private getColors(
		color: { r?: number; g?: number; b?: number } | Array<{ r?: number; g?: number; b?: number }>,
		length: number
	): Array<Color4> {
		return Array.isArray(color)
			? color.map((c) => new Color4(c.r, c.g, c.b, 1))
			: this.createColorsArray(color, length);
	}

	private createColorsArray(
		color: { r?: number; g?: number; b?: number },
		length: number
	): Array<Color4> {
		const a = new Array<Color4>();
		for (let i = 0; i < length; i++) {
			a.push(new Color4(color.r, color.g, color.b, 1)); // TODO pass visibility or alpha prop
		}
		return a;
	}
}
